import { Button, Flex, Input, Modal, Typography } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { BlueSelected, White } from '../../styles';
import { questionType } from '../../types';

const { Text } = Typography;

const StyledText = styled(Text)`
  font-size: 12px;
  font-weight: 400;
`;

const StyledInput = styled(Input)`
  margin: 8px 0;
`;

const ScrollableFlex = styled(Flex)`
  overflow-y: auto;
  max-height: calc(100vh - 350px);
`;

const StyledDiv = styled.div`
  padding: 8px 12px;
  margin: 2px 0;
  border-radius: 6px;
  cursor: pointer;
  background: ${(props) => (props.$selected ? BlueSelected : White)};
`;

function DisplayAttributesModal({ visible, setVisible, attributes, onOk, selectedAttributeIds }) {
  const [pendingAttributeIds, setPendingAttributeIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [displayError, setDisplayError] = useState(false);

  useEffect(() => {
    if (visible) {
      setPendingAttributeIds(selectedAttributeIds);
    }
  }, [selectedAttributeIds, visible]);

  const onAttributeClick = (id) => {
    if (pendingAttributeIds.includes(id)) {
      setPendingAttributeIds(pendingAttributeIds.filter((aId) => aId !== id));
      setDisplayError(false);
    } else if (pendingAttributeIds.length < 5) {
      setPendingAttributeIds([...pendingAttributeIds, id]);
    } else {
      setDisplayError(true);
    }
  };

  const handleClearAll = () => {
    setPendingAttributeIds([]);
    setDisplayError(false);
  };

  const filteredAttributes = attributes
    .filter((attr) => attr.text.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => a.text.localeCompare(b.text));

  const renderTitle = () => (
    <Flex justify="space-between" align="center">
      Select attributes to display
      {displayError && <StyledText type="danger">Max of 5 attributes allowed</StyledText>}
    </Flex>
  );

  return (
    <Modal
      title={renderTitle()}
      open={visible}
      destroyOnClose
      maskClosable={false}
      closable={false}
      footer={[
        <Flex key="footer" justify="space-between" align="center">
          <Button danger disabled={pendingAttributeIds.length === 0} onClick={handleClearAll}>
            Clear all
          </Button>
          <Flex gap="small">
            <Button
              onClick={() => {
                setVisible(false);
                setDisplayError(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setVisible(false);
                onOk(pendingAttributeIds);
                setDisplayError(false);
              }}
            >
              OK
            </Button>
          </Flex>
        </Flex>,
      ]}
    >
      <StyledInput
        placeholder="Search attributes..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        allowClear
      />
      <ScrollableFlex vertical>
        {filteredAttributes
          .sort((a, b) => a.text.localeCompare(b.text))
          .map(({ id, text }) => (
            <StyledDiv
              key={id}
              onClick={() => onAttributeClick(id)}
              $selected={pendingAttributeIds.includes(id)}
            >
              {text}
            </StyledDiv>
          ))}
      </ScrollableFlex>
    </Modal>
  );
}

DisplayAttributesModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  attributes: PropTypes.arrayOf(questionType).isRequired,
  onOk: PropTypes.func.isRequired,
  selectedAttributeIds: PropTypes.arrayOf(PropTypes.number).isRequired,
};

export default DisplayAttributesModal;
